import React, { useState, useMemo } from 'react';
import {
  Calendar, Users, Target, Clock, TrendingUp, Play, Pause, Square, Edit,
  MoreHorizontal, Plus, ChevronDown, ChevronRight, Search, Filter,
  GripVertical, ArrowRight, CheckCircle2, AlertTriangle, Eye, X
} from 'lucide-react';
import { UserAvatar } from '../UI/UserAvatar';
import { Sprint, Issue, User, Project, SprintStats } from '../../types';

interface SprintBoardProps {
  sprints: Sprint[];
  issues: Issue[];
  users: User[];
  projects: Project[];
  currentUser: User;
  onCreateSprint: () => void;
  onEditSprint: (sprint: Sprint) => void;
  onStartSprint: (sprintId: string) => void;
  onCompleteSprint: (sprintId: string) => void;
  onAddIssueToSprint: (sprintId: string, issueId: string) => void;
  onRemoveIssueFromSprint: (sprintId: string, issueId: string) => void;
  onIssueClick?: (issue: Issue) => void;
}

export const SprintBoard: React.FC<SprintBoardProps> = ({
  sprints,
  issues,
  users,
  projects,
  currentUser,
  onCreateSprint,
  onEditSprint,
  onStartSprint,
  onCompleteSprint,
  onAddIssueToSprint,
  onRemoveIssueFromSprint,
  onIssueClick,
}) => {
  const [activeView, setActiveView] = useState<'backlog' | 'board'>('backlog');
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSprints, setExpandedSprints] = useState<Set<string>>(new Set(sprints.filter(s => s.state === 'active').map(s => s.id)));
  const [showBacklog, setShowBacklog] = useState(true);
  const [draggedIssue, setDraggedIssue] = useState<Issue | null>(null);

  // Filter sprints by project
  const filteredSprints = useMemo(() => {
    let result = [...sprints];
    if (selectedProjectFilter !== 'all') {
      result = result.filter(s => s.projectId === selectedProjectFilter);
    }
    return result.sort((a, b) => {
      const order = { active: 0, future: 1, closed: 2 };
      return (order[a.state] || 3) - (order[b.state] || 3);
    });
  }, [sprints, selectedProjectFilter]);

  // Get all issue keys that are in any sprint
  const issuesInSprints = useMemo(() => {
    const keys = new Set<string>();
    sprints.forEach(s => s.issues.forEach(k => keys.add(k)));
    return keys;
  }, [sprints]);

  // Backlog issues (not in any sprint)
  const backlogIssues = useMemo(() => {
    let result = issues.filter(i => !issuesInSprints.has(i.key));
    if (selectedProjectFilter !== 'all') {
      result = result.filter(i => i.projectId === selectedProjectFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i => i.summary.toLowerCase().includes(q) || i.key.toLowerCase().includes(q));
    }
    return result;
  }, [issues, issuesInSprints, selectedProjectFilter, searchQuery]);

  // Active sprint for board view
  const activeSprint = useMemo(() => {
    return filteredSprints.find(s => s.state === 'active') || null;
  }, [filteredSprints]);

  // Issues for active sprint board
  const activeSprintIssues = useMemo(() => {
    if (!activeSprint) return [];
    return issues.filter(i => activeSprint.issues.includes(i.key));
  }, [activeSprint, issues]);

  const getSprintStats = (sprint: Sprint): SprintStats => {
    const sprintIssues = issues.filter(issue => sprint.issues.includes(issue.key));
    const completedIssues = sprintIssues.filter(issue => issue.status === 'done');
    const inProgressIssues = sprintIssues.filter(issue => issue.status === 'in-progress');
    const todoIssues = sprintIssues.filter(issue => ['to-do', 'backlog'].includes(issue.status));
    const totalStoryPoints = sprintIssues.reduce((sum, issue) => sum + (issue.storyPoints || 0), 0);
    const completedStoryPoints = completedIssues.reduce((sum, issue) => sum + (issue.storyPoints || 0), 0);
    return {
      totalIssues: sprintIssues.length,
      completedIssues: completedIssues.length,
      inProgressIssues: inProgressIssues.length,
      todoIssues: todoIssues.length,
      totalStoryPoints,
      completedStoryPoints,
      remainingStoryPoints: totalStoryPoints - completedStoryPoints,
      velocityTrend: [8, 12, 15, 18, 22],
    };
  };

  const getSprintProgress = (sprint: Sprint): number => {
    const stats = getSprintStats(sprint);
    if (stats.totalIssues === 0) return 0;
    return Math.round((stats.completedIssues / stats.totalIssues) * 100);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const getDaysRemaining = (endDate: string) => {
    const diffDays = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const toggleSprint = (sprintId: string) => {
    setExpandedSprints(prev => {
      const next = new Set(prev);
      if (next.has(sprintId)) next.delete(sprintId);
      else next.add(sprintId);
      return next;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'in-review': return 'bg-purple-100 text-purple-700';
      case 'to-do': return 'bg-gray-100 text-gray-700';
      case 'backlog': return 'bg-gray-50 text-gray-500';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'highest': return <ArrowRight className="w-3.5 h-3.5 text-red-600 rotate-[-90deg]" />;
      case 'high': return <ArrowRight className="w-3.5 h-3.5 text-orange-500 rotate-[-45deg]" />;
      case 'medium': return <ArrowRight className="w-3.5 h-3.5 text-yellow-500" />;
      case 'low': return <ArrowRight className="w-3.5 h-3.5 text-green-500 rotate-[45deg]" />;
      case 'lowest': return <ArrowRight className="w-3.5 h-3.5 text-blue-400 rotate-[90deg]" />;
      default: return <ArrowRight className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bug': return 'bg-red-500';
      case 'story': return 'bg-green-500';
      case 'task': return 'bg-blue-500';
      case 'epic': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getSprintStateColor = (state: string) => {
    switch (state) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'future': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'closed': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, issue: Issue) => {
    setDraggedIssue(issue);
    e.dataTransfer.setData('text/plain', issue.key);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSprint = (e: React.DragEvent, sprintId: string) => {
    e.preventDefault();
    const issueKey = e.dataTransfer.getData('text/plain');
    if (issueKey && draggedIssue) {
      onAddIssueToSprint(sprintId, draggedIssue.id);
    }
    setDraggedIssue(null);
  };

  const handleDropOnBacklog = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedIssue) {
      const sprint = sprints.find(s => s.issues.includes(draggedIssue.key));
      if (sprint) {
        onRemoveIssueFromSprint(sprint.id, draggedIssue.id);
      }
    }
    setDraggedIssue(null);
  };

  // Issue row component
  const IssueRow: React.FC<{ issue: Issue; showProject?: boolean }> = ({ issue, showProject = false }) => {
    const assignee = users.find(u => u.id === (issue.assignee || issue.assignees?.[0]));
    const project = projects.find(p => p.id === issue.projectId);
    return (
      <div
        className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50/50 border-b border-gray-100 cursor-pointer group transition-colors"
        draggable
        onDragStart={(e) => handleDragStart(e, issue)}
        onClick={() => onIssueClick?.(issue)}
      >
        <GripVertical className="w-3.5 h-3.5 text-gray-300 opacity-0 group-hover:opacity-100 cursor-grab shrink-0" />
        <div className={`w-1 h-4 rounded-full ${getTypeColor(issue.type)} shrink-0`} />
        <span className="text-2xs font-mono font-medium text-blue-700 w-16 shrink-0">{issue.key}</span>
        <p className="text-xs text-gray-900 flex-1 truncate">{issue.summary}</p>
        {showProject && project && (
          <span className="text-2xs text-gray-400 shrink-0">{project.key}</span>
        )}
        {getPriorityIcon(issue.priority)}
        <span className={`px-1.5 py-0.5 rounded text-2xs font-medium capitalize shrink-0 ${getStatusColor(issue.status)}`}>
          {issue.status.replace('-', ' ')}
        </span>
        {issue.storyPoints && (
          <span className="w-5 h-5 rounded-full bg-gray-100 text-2xs font-bold text-gray-600 flex items-center justify-center shrink-0">{issue.storyPoints}</span>
        )}
        {assignee ? (
          <UserAvatar name={assignee.displayName} src={assignee.avatarUrl} className="w-5 h-5 shrink-0" />
        ) : (
          <div className="w-5 h-5 rounded-full bg-gray-200 shrink-0" />
        )}
      </div>
    );
  };

  // Board columns for active sprint
  const boardColumns = [
    { id: 'to-do', label: 'TO DO', color: 'border-gray-300', bgColor: 'bg-gray-50' },
    { id: 'in-progress', label: 'IN PROGRESS', color: 'border-blue-400', bgColor: 'bg-blue-50/30' },
    { id: 'in-review', label: 'IN REVIEW', color: 'border-purple-400', bgColor: 'bg-purple-50/30' },
    { id: 'done', label: 'DONE', color: 'border-green-400', bgColor: 'bg-green-50/30' },
  ];

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Sprints</h2>
            <p className="text-xs text-gray-500 mt-0.5">{filteredSprints.length} sprints • {backlogIssues.length} backlog items</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCreateSprint}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors text-xs font-medium"
            >
              <Plus className="w-3.5 h-3.5" />
              Create Sprint
            </button>
          </div>
        </div>

        {/* View toggle + Filters */}
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setActiveView('backlog')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeView === 'backlog' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Backlog
            </button>
            <button
              onClick={() => setActiveView('board')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeView === 'board' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Board
            </button>
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
          </div>

          <div className="relative">
            <select
              value={selectedProjectFilter}
              onChange={(e) => setSelectedProjectFilter(e.target.value)}
              className="appearance-none px-3 py-1.5 pr-7 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
            >
              <option value="all">All Projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Backlog View */}
      {activeView === 'backlog' && (
        <div className="flex-1 overflow-y-auto">
          {/* Sprint Sections */}
          {filteredSprints.map(sprint => {
            const stats = getSprintStats(sprint);
            const progress = getSprintProgress(sprint);
            const isExpanded = expandedSprints.has(sprint.id);
            const sprintIssues = issues.filter(i => sprint.issues.includes(i.key));
            const project = projects.find(p => p.id === sprint.projectId);
            const daysLeft = sprint.endDate ? getDaysRemaining(sprint.endDate) : null;

            return (
              <div
                key={sprint.id}
                className={`border-b border-gray-200 ${sprint.state === 'active' ? 'bg-blue-50/20' : ''}`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnSprint(e, sprint.id)}
              >
                {/* Sprint Header */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => toggleSprint(sprint.id)}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}

                  <h3 className="text-sm font-semibold text-gray-900">{sprint.name}</h3>

                  <span className={`px-2 py-0.5 rounded text-2xs font-semibold border ${getSprintStateColor(sprint.state)}`}>
                    {sprint.state.toUpperCase()}
                  </span>

                  {project && <span className="text-2xs text-gray-400">{project.key}</span>}

                  {sprint.startDate && sprint.endDate && (
                    <span className="text-2xs text-gray-400">
                      {formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}
                    </span>
                  )}

                  {sprint.state === 'active' && daysLeft !== null && (
                    <span className={`text-2xs font-medium ${daysLeft <= 3 ? 'text-red-600' : daysLeft <= 7 ? 'text-orange-600' : 'text-gray-500'}`}>
                      {daysLeft > 0 ? `${daysLeft}d left` : 'Overdue'}
                    </span>
                  )}

                  <div className="flex-1" />

                  {/* Stats pills */}
                  <div className="flex items-center gap-2">
                    <span className="text-2xs text-gray-500">{stats.totalIssues} issues</span>
                    {stats.totalStoryPoints > 0 && (
                      <span className="text-2xs text-gray-500">{stats.completedStoryPoints}/{stats.totalStoryPoints} pts</span>
                    )}
                    {stats.totalIssues > 0 && (
                      <div className="w-20 bg-gray-200 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {sprint.state === 'future' && (
                      <button onClick={() => onStartSprint(sprint.id)} className="flex items-center gap-1 px-2 py-1 text-2xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded transition-colors">
                        <Play className="w-3 h-3" /> Start
                      </button>
                    )}
                    {sprint.state === 'active' && (
                      <button onClick={() => onCompleteSprint(sprint.id)} className="flex items-center gap-1 px-2 py-1 text-2xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors">
                        <CheckCircle2 className="w-3 h-3" /> Complete
                      </button>
                    )}
                    <button onClick={() => onEditSprint(sprint)} className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors">
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Sprint Issues */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {sprint.goal && (
                      <div className="px-10 py-2 bg-yellow-50/50 border-b border-gray-100">
                        <p className="text-2xs text-gray-600"><span className="font-semibold text-gray-700">Goal:</span> {sprint.goal}</p>
                      </div>
                    )}
                    {sprintIssues.length > 0 ? (
                      sprintIssues.map(issue => (
                        <IssueRow key={issue.id} issue={issue} showProject={selectedProjectFilter === 'all'} />
                      ))
                    ) : (
                      <div className="px-10 py-6 text-center">
                        <p className="text-xs text-gray-400">No issues in this sprint. Drag issues here from the backlog.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Backlog Section */}
          <div
            className="border-b border-gray-200"
            onDragOver={handleDragOver}
            onDrop={handleDropOnBacklog}
          >
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors bg-gray-50/30"
              onClick={() => setShowBacklog(!showBacklog)}
            >
              {showBacklog ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              <h3 className="text-sm font-semibold text-gray-700">Backlog</h3>
              <span className="text-2xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded font-medium">{backlogIssues.length}</span>
              <div className="flex-1" />
            </div>

            {showBacklog && (
              <div className="border-t border-gray-100">
                {backlogIssues.length > 0 ? (
                  backlogIssues.map(issue => (
                    <IssueRow key={issue.id} issue={issue} showProject={selectedProjectFilter === 'all'} />
                  ))
                ) : (
                  <div className="px-10 py-6 text-center">
                    <p className="text-xs text-gray-400">No backlog items{selectedProjectFilter !== 'all' ? ' for this project' : ''}.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Empty state */}
          {filteredSprints.length === 0 && backlogIssues.length === 0 && (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-gray-700 mb-1">No sprints yet</h3>
                <p className="text-xs text-gray-500 mb-4">Create your first sprint to start planning</p>
                <button onClick={onCreateSprint} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors">
                  Create Sprint
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Board View */}
      {activeView === 'board' && (
        <div className="flex-1 overflow-hidden">
          {activeSprint ? (
            <div className="h-full flex flex-col">
              {/* Active Sprint Info Bar */}
              <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs font-semibold text-gray-900">{activeSprint.name}</span>
                </div>
                {activeSprint.endDate && (
                  <span className="text-2xs text-gray-500">
                    {getDaysRemaining(activeSprint.endDate) > 0
                      ? `${getDaysRemaining(activeSprint.endDate)} days remaining`
                      : 'Sprint overdue'}
                  </span>
                )}
                <div className="flex-1" />
                <div className="flex items-center gap-3">
                  <span className="text-2xs text-gray-500">{activeSprintIssues.length} issues</span>
                  <div className="w-24 bg-blue-200 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-blue-600 transition-all" style={{ width: `${getSprintProgress(activeSprint)}%` }} />
                  </div>
                  <span className="text-2xs font-semibold text-blue-700">{getSprintProgress(activeSprint)}%</span>
                </div>
              </div>

              {/* Board Columns */}
              <div className="flex-1 flex gap-4 p-4 overflow-x-auto">
                {boardColumns.map(col => {
                  const colIssues = activeSprintIssues.filter(i => {
                    if (col.id === 'to-do') return i.status === 'to-do' || i.status === 'backlog';
                    return i.status === col.id;
                  });
                  return (
                    <div key={col.id} className={`flex-1 min-w-[250px] flex flex-col rounded-xl ${col.bgColor} border border-gray-200`}>
                      {/* Column Header */}
                      <div className={`px-3 py-2.5 border-b-2 ${col.color} flex items-center justify-between`}>
                        <div className="flex items-center gap-2">
                          <span className="text-2xs font-bold text-gray-600 uppercase tracking-wider">{col.label}</span>
                          <span className="text-2xs font-bold text-gray-400 bg-white px-1.5 py-0.5 rounded">{colIssues.length}</span>
                        </div>
                      </div>

                      {/* Column Issues */}
                      <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {colIssues.map(issue => {
                          const assignee = users.find(u => u.id === (issue.assignee || issue.assignees?.[0]));
                          return (
                            <div
                              key={issue.id}
                              className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer group"
                              onClick={() => onIssueClick?.(issue)}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-1.5 h-1.5 rounded-full ${getTypeColor(issue.type)}`} />
                                <span className="text-2xs font-mono font-medium text-blue-600">{issue.key}</span>
                                <div className="flex-1" />
                                {getPriorityIcon(issue.priority)}
                              </div>
                              <p className="text-xs text-gray-900 mb-2 line-clamp-2">{issue.summary}</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  {issue.storyPoints && (
                                    <span className="w-5 h-5 rounded-full bg-gray-100 text-2xs font-bold text-gray-600 flex items-center justify-center">{issue.storyPoints}</span>
                                  )}
                                  {issue.labels.slice(0, 1).map(label => (
                                    <span key={label} className="text-2xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{label}</span>
                                  ))}
                                </div>
                                {assignee ? (
                                  <UserAvatar name={assignee.displayName} src={assignee.avatarUrl} className="w-5 h-5" />
                                ) : (
                                  <div className="w-5 h-5 rounded-full bg-gray-200" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="text-center">
                <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-gray-700 mb-1">No active sprint</h3>
                <p className="text-xs text-gray-500 mb-4">Start a sprint from the backlog view to see the board</p>
                <button
                  onClick={() => setActiveView('backlog')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors"
                >
                  Go to Backlog
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
